import { useMemo } from 'react'
import { Player } from '../types'
import { useAuthStore, usePlayerStore } from '../stores'
import {
  GitCommitHorizontal, Building2, Trophy, ArrowRightLeft,
  Calendar, Target, Star
} from 'lucide-react'

interface PlayerCareerTimelineProps {
  player?: Player
}

function PlayerCareerTimeline({ player: playerProp }: PlayerCareerTimelineProps) {
  const { user } = useAuthStore()
  const { players } = usePlayerStore()

  const currentPlayer = useMemo(() => {
    if (playerProp) return playerProp
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [playerProp, user, players])

  const timelineEntries = useMemo(() => {
    if (!currentPlayer) return []

    const entries: Array<{
      id: string
      type: 'club' | 'academy' | 'current'
      name: string
      years: string
      goals?: number
      assists?: number
      fee?: string
      competition?: string
      startYear?: number
    }> = []

    const careerHistory = currentPlayer.career_history || []
    careerHistory.forEach((entry) => {
      const yearMatch = entry.years?.match(/(\d{4})/)
      const startYear = yearMatch ? parseInt(yearMatch[1]) : undefined

      entries.push({
        id: entry.id,
        type: 'club',
        name: entry.club,
        years: entry.years || 'Unknown',
        goals: entry.goals,
        assists: entry.assists,
        fee: entry.transaction_fee,
        startYear,
      })
    })

    if (currentPlayer.club) {
      const currentYear = new Date().getFullYear()
      entries.push({
        id: 'current',
        type: 'current',
        name: currentPlayer.club,
        years: `${currentYear} - Present`,
        goals: currentPlayer.goals,
        assists: currentPlayer.assists,
        startYear: currentYear,
      })
    }

    const competitions = currentPlayer.competition_performances || []
    const competitionNames = new Set<string>()
    competitions.forEach((comp) => {
      if (comp.competition_name && !competitionNames.has(comp.competition_name)) {
        competitionNames.add(comp.competition_name)
        entries.push({
          id: `comp-${comp.competition_name}`,
          type: 'academy' as const,
          name: comp.competition_name,
          years: comp.appearance_date ? new Date(comp.appearance_date).getFullYear().toString() : 'Recent',
          competition: comp.competition_name,
        })
      }
    })

    entries.sort((a, b) => (b.startYear || 0) - (a.startYear || 0))

    return entries
  }, [currentPlayer])

  const getEntryIcon = (type: string) => {
    switch (type) {
      case 'current': return <Star size={16} className="text-green-500" />
      case 'academy': return <Target size={16} className="text-purple-500" />
      default: return <Building2 size={16} className="text-blue-500" />
    }
  }

  const getEntryColor = (type: string) => {
    switch (type) {
      case 'current': return 'border-green-500/30 bg-green-500/5'
      case 'academy': return 'border-purple-500/30 bg-purple-500/5'
      default: return 'border-gray-700 bg-white/5'
    }
  }

  if (!currentPlayer) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
        <GitCommitHorizontal className="mx-auto text-gray-500 mb-4" size={48} />
        <h3 className="text-lg font-semibold text-white">No Career Data</h3>
        <p className="text-gray-400 mt-2">Your career timeline will appear here once data is recorded.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Career Timeline</h2>
          <p className="text-sm text-gray-400 mt-1">Your football journey — clubs, academies, and milestones</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-green-500" /> Current
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-blue-500" /> Club
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-purple-500" /> Competition
          </div>
        </div>
      </div>

      {/* Timeline */}
      {timelineEntries.length > 0 ? (
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-800" />

          <div className="space-y-4">
            {timelineEntries.map((entry) => (
              <div key={entry.id} className="relative flex items-start gap-4 pl-2">
                {/* Timeline Dot */}
                <div className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center ${getEntryColor(entry.type)} border`}>
                  {getEntryIcon(entry.type)}
                </div>

                {/* Content Card */}
                <div className={`flex-1 rounded-2xl border p-4 ${getEntryColor(entry.type)} ${entry.type === 'current' ? 'ring-1 ring-green-500/20' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-white truncate">{entry.name}</h3>
                        {entry.type === 'current' && (
                          <span className="text-[10px] font-bold text-green-400 uppercase tracking-widest bg-green-500/10 px-2 py-0.5 rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500 uppercase tracking-widest">
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />
                          {entry.years}
                        </span>
                        {entry.competition && (
                          <span className="flex items-center gap-1">
                            <Trophy size={10} className="text-yellow-500" />
                            {entry.competition}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    {(entry.goals !== undefined || entry.assists !== undefined) && (
                      <div className="flex items-center gap-3 ml-4">
                        {entry.goals !== undefined && (
                          <div className="text-center">
                            <p className="text-lg font-black text-green-400">{entry.goals}</p>
                            <p className="text-[8px] text-gray-500 uppercase tracking-widest">Goals</p>
                          </div>
                        )}
                        {entry.assists !== undefined && (
                          <div className="text-center">
                            <p className="text-lg font-black text-blue-400">{entry.assists}</p>
                            <p className="text-[8px] text-gray-500 uppercase tracking-widest">Assists</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {entry.fee && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] text-gray-500">
                      <ArrowRightLeft size={10} />
                      <span>Transfer: {entry.fee}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-8 text-center">
          <GitCommitHorizontal className="mx-auto text-gray-500 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white">No Career History</h3>
          <p className="text-gray-400 mt-2 text-sm">
            Your career timeline will be built from your club history and competition data.
          </p>
          <p className="text-gray-500 mt-1 text-xs">
            Add career history in your Profile to see it here.
          </p>
        </div>
      )}
    </div>
  )
}

export default PlayerCareerTimeline
